import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const services = [
    { name: 'Roofing', slug: 'roofing', icon: 'ri-home-8-line' },
    { name: 'Siding', slug: 'siding', icon: 'ri-building-line' },
    { name: 'Gutters', slug: 'gutters', icon: 'ri-water-flash-line' },
    { name: 'Windows', slug: 'windows', icon: 'ri-window-line' },
    { name: 'Doors', slug: 'doors', icon: 'ri-door-open-line' },
    { name: 'Storm Damage', slug: 'storm-damage', icon: 'ri-flashlight-line' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/e8d9864e0d1c345c04aa52accd571aeb.png"
              alt="Spyder Restoration"
              className="h-16 drop-shadow-lg"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <div className="relative group">
              <button
                className={`flex items-center gap-1 font-semibold transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                Services <i className="ri-arrow-down-s-line"></i>
              </button>
              
              {/* Dropdown */}
              {isServicesOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl py-4 border border-gray-100"
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  {services.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="flex items-center gap-3 px-6 py-3 hover:bg-cyan-50 transition-colors cursor-pointer"
                    >
                      <i className={`${service.icon} text-cyan-600 text-xl`}></i>
                      <span className="text-gray-900 font-medium">{service.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/about" className={`font-semibold transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}>
              About
            </Link>
            <Link to="/careers" className={`font-semibold transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}>
              Careers
            </Link>
            <a href="/#quote" className={`font-semibold transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}>
              Contact
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:5743503050"
              className={`px-6 py-2 font-bold rounded-full transition-all whitespace-nowrap cursor-pointer ${isScrolled ? 'bg-cyan-500 text-white hover:bg-cyan-600' : 'bg-white text-gray-900 hover:bg-gray-100'}`}
            >
              <i className="ri-phone-fill mr-2"></i>574-350-3050
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 rounded-lg cursor-pointer ${isScrolled ? 'text-gray-900' : 'text-white'}`}
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-3xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-4">
            <div>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="flex items-center justify-between w-full text-gray-900 font-semibold py-2 cursor-pointer"
              >
                Services <i className={`ri-arrow-${isServicesOpen ? 'up' : 'down'}-s-line`}></i>
              </button>
              {isServicesOpen && (
                <div className="pl-4 mt-2 space-y-2">
                  {services.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-2 text-gray-700 py-2 cursor-pointer"
                    >
                      <i className={`${service.icon} text-cyan-600`}></i>
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/about" onClick={() => setIsMenuOpen(false)} className="block text-gray-900 font-semibold py-2 cursor-pointer">
              About
            </Link>
            <Link to="/careers" onClick={() => setIsMenuOpen(false)} className="block text-gray-900 font-semibold py-2 cursor-pointer">
              Careers
            </Link>
            <a href="/#quote" onClick={() => setIsMenuOpen(false)} className="block text-gray-900 font-semibold py-2 cursor-pointer">
              Contact
            </a>
            <a
              href="tel:5743503050"
              className="block w-full text-center px-6 py-3 bg-cyan-500 text-white font-bold rounded-full hover:bg-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2"></i>574-350-3050
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
